import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { HR_CATEGORIES, HR_CATEGORY_ORDER, HR_ROLES, rolesForCategory, periodHint } from "@/lib/hr";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UploadCloud, Loader2, Plus, Trash2, CheckCircle2, AlertTriangle, RefreshCw, ImageIcon, Mail, Inbox, Settings, Maximize2, Minus } from "lucide-react";

function PageImg({ jobId, sourceId, page, className }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url;
    setSrc(null); setErr(false);
    api.get(`/hrdoc/uploads/${jobId}/sources/${sourceId}/page/${page}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setErr(true));
    return () => url && URL.revokeObjectURL(url);
  }, [jobId, sourceId, page]);
  if (err) return <div className="text-xs text-muted-foreground p-4 text-center">Voorbeeld niet beschikbaar</div>;
  if (!src) return <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  return <img src={src} alt={`pagina ${page}`} className={className || "w-full rounded border"} />;
}

export default function HRUpload() {
  const [files, setFiles] = useState([]);
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showSkipped, setShowSkipped] = useState(false);
  const [preview, setPreview] = useState(null); // {sourceId, start, end}
  const [zoom, setZoom] = useState(100);
  const [queue, setQueue] = useState([]);
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: "", imap_host: "imap.gmail.com", app_password: "", enabled: false });
  const [checking, setChecking] = useState(false);
  const fileRef = useRef();
  const poll = useRef();

  const loadQueue = () => api.get("/hrdoc/uploads").then((r) => setQueue(r.data)).catch(() => {});
  const loadEmailCfg = () => api.get("/hrdoc/email/config").then((r) => setEmailCfg(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/hrdoc/employees", { params: { size: 100 } }).then((r) => setEmps(r.data.items));
    loadQueue();
    loadEmailCfg();
    return () => clearInterval(poll.current);
  }, []);

  const startUpload = async () => {
    if (files.length === 0) return toast.error("Kies één of meer PDF-bestanden");
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/hrdoc/uploads", fd);
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      watch(data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const watch = (jobId) => {
    clearInterval(poll.current);
    let attempts = 0;
    poll.current = setInterval(async () => {
      attempts += 1;
      if (attempts > 120) { // ~3 min safeguard against a stuck job
        clearInterval(poll.current);
        setBusy(false);
        toast.error("Analyse duurt te lang. Probeer 'Opnieuw analyseren'.");
        setJob({ status: "failed", id: jobId, error: "Analyse reageert niet meer." });
        return;
      }
      const { data } = await api.get(`/hrdoc/uploads/${jobId}`);
      if (data.status !== "analyzing") {
        clearInterval(poll.current);
        setBusy(false);
        setJob(data);
        if (data.status === "review") {
          const source = data.review_draft || data.rows;
          setRows(source.map((r) => ({ ...r, reviewed: r.reviewed || false, skip: r.skip || false })));
        }
      }
    }, 1500);
  };

  const update = (rid, patch) => setRows((rs) => rs.map((r) => (r.row_id === rid ? { ...r, ...patch } : r)));

  const changeCategory = (rid, cat) => {
    const roles = rolesForCategory(cat);
    update(rid, { category: cat, document_role: roles[0], document_type: `${cat}_${roles[0]}` });
  };

  const addRow = (source) => {
    setRows((rs) => [...rs, {
      row_id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      source_id: source.id, source_name: source.name, start_page: 1, end_page: 1,
      employee_id: "", category: "", document_role: "", document_type: "", period_key: "",
      document_date: "", name: "", reviewed: false, skip: false, warnings: ["Handmatig toegevoegd"], confidence: 0,
    }]);
  };

  const removeRow = (rid) => deleteRows([rid]);

  // Delete files: newly-added split rows are removed outright; analyzed rows are kept as
  // "not saved" (skip) so the source's pages stay fully covered at commit.
  const deleteRows = (ids) => {
    const set = new Set(ids);
    setRows((rs) => rs.flatMap((r) => (set.has(r.row_id)
      ? (String(r.row_id).startsWith("new-") ? [] : [{ ...r, skip: true, reviewed: true }])
      : [r])));
    setSelected(new Set());
  };
  const restoreRow = (rid) => setRows((rs) => rs.map((r) => (r.row_id === rid ? { ...r, skip: false } : r)));
  const toggleSelect = (rid) => setSelected((s) => { const n = new Set(s); n.has(rid) ? n.delete(rid) : n.add(rid); return n; });

  const saveDraft = async () => {
    try {
      await api.put(`/hrdoc/uploads/${job.id}/review`, { rows });
      toast.success("Concept bewaard");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/hrdoc/uploads/${job.id}/commit`, { rows });
      toast.success(`${data.count} document(en) opgeslagen`);
      reset();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  const reset = () => { setJob(null); setRows([]); clearInterval(poll.current); loadQueue(); loadEmailCfg(); };

  const openJob = async (id) => {
    const { data } = await api.get(`/hrdoc/uploads/${id}`);
    if (data.status === "analyzing") { setBusy(true); setJob(data); watch(id); }
    else if (data.status === "review") { setJob(data); const src = data.review_draft || data.rows; setRows(src.map((r) => ({ ...r, reviewed: r.reviewed || false, skip: r.skip || false }))); }
    else if (data.status === "failed") { setJob(data); }
    else toast.info("Deze upload is al opgeslagen.");
  };

  const openEmailSettings = () => {
    setEmailForm({ email: emailCfg?.email || "singhexpress68@gmail.com", imap_host: emailCfg?.imap_host || "imap.gmail.com", app_password: "", enabled: emailCfg?.enabled || false });
    setEmailOpen(true);
  };
  const saveEmail = async () => {
    try {
      await api.post("/hrdoc/email/config", emailForm);
      toast.success("E-mailinstellingen bewaard");
      setEmailOpen(false);
      loadEmailCfg();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const checkEmail = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/hrdoc/email/check");
      toast.success(`${data.jobs_created || 0} nieuwe e-mail(s) verwerkt`);
      loadQueue();
      loadEmailCfg();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  const JOB_STATUS = {
    analyzing: { label: "Bezig met analyseren", cls: "bg-blue-100 text-blue-700" },
    review: { label: "Klaar om te controleren", cls: "bg-amber-100 text-amber-700" },
    committed: { label: "Opgeslagen", cls: "bg-emerald-100 text-emerald-700" },
    failed: { label: "Mislukt", cls: "bg-red-100 text-red-700" },
    saving: { label: "Opslaan…", cls: "bg-blue-100 text-blue-700" },
  };

  // -------- initial upload screen --------
  if (!job) {
    return (
      <div className="space-y-6" data-testid="hr-upload">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Documenten uploaden</h1>
          <p className="text-muted-foreground mt-1">Sleep loonbrieven, stortingen, contracten of andere PDF's. De app leest elke pagina uit (tekst of scan/OCR) en groepeert ze per werknemer en periode. Daarna controleer je alles vóór opslaan.</p>
        </div>
        <Card>
          <CardContent className="p-8">
            <label className="border-2 border-dashed border-border rounded-xl p-10 flex flex-col items-center justify-center text-center cursor-pointer hover:border-teal-500 transition-colors block">
              <UploadCloud className="h-10 w-10 text-teal-600 mb-3" />
              <span className="font-medium">Kies PDF-bestanden</span>
              <span className="text-sm text-muted-foreground mt-1">Max. 40 bestanden / 150 MB per batch</span>
              <input ref={fileRef} type="file" accept="application/pdf" multiple className="hidden"
                     data-testid="hr-file-input" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </label>
            {files.length > 0 && (
              <div className="mt-4 text-sm text-muted-foreground" data-testid="hr-file-count">{files.length} bestand(en) geselecteerd</div>
            )}
            <Button className="mt-5 bg-teal-600 hover:bg-teal-700" disabled={busy || files.length === 0} onClick={startUpload} data-testid="hr-analyze-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyseren…</> : <>Analyseren</>}
            </Button>
          </CardContent>
        </Card>

        {/* HR via e-mail */}
        <Card data-testid="hr-email-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex gap-3">
                <div className="h-10 w-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Mail className="h-5 w-5" /></div>
                <div>
                  <div className="font-medium">HR-documenten via e-mail</div>
                  <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">
                    Stuur documenten als bijlage (PDF of foto) naar <span className="font-medium text-foreground">{emailCfg?.email || "singhexpress68@gmail.com"}</span> met <span className="font-medium text-foreground">HR</span> in het onderwerp. Ze verschijnen dan hieronder in de wachtrij om te controleren.
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {emailCfg?.configured
                      ? <Badge className={emailCfg.enabled ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}>{emailCfg.enabled ? "Automatisch ophalen aan" : "Uitgeschakeld"}</Badge>
                      : <Badge variant="outline" className="text-amber-600 border-amber-300">Nog niet ingesteld</Badge>}
                    <Badge variant="outline" className={emailCfg?.automation_active ? "text-emerald-600 border-emerald-300" : "text-amber-600 border-amber-300"}>
                      {emailCfg?.automation_active ? "Live: draait automatisch" : "Alleen handmatig (testomgeving)"}
                    </Badge>
                    {emailCfg?.last_error && <span className="text-xs text-red-600">{emailCfg.last_error}</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={openEmailSettings} data-testid="hr-email-settings-btn"><Settings className="h-4 w-4 mr-2" /> Instellingen</Button>
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700" disabled={checking || !emailCfg?.configured} onClick={checkEmail} data-testid="hr-email-check-btn">
                  {checking ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Controleren…</> : <><RefreshCw className="h-4 w-4 mr-2" /> Nu controleren</>}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wachtrij */}
        {queue.length > 0 && (
          <Card data-testid="hr-queue-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3 font-medium"><Inbox className="h-4 w-4 text-muted-foreground" /> Wachtrij</div>
              <div className="divide-y">
                {queue.map((j) => {
                  const st = JOB_STATUS[j.status] || JOB_STATUS.analyzing;
                  return (
                    <div key={j.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`hr-queue-${j.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {j.source === "email" ? <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {j.email_subject || "E-mail"}</span> : "Handmatige upload"}
                        </div>
                        <div className="text-xs text-muted-foreground">{j.source === "email" && j.email_sender ? j.email_sender : ""}{j.saved_count ? ` · ${j.saved_count} opgeslagen` : ""}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={st.cls}>{st.label}</Badge>
                        {(j.status === "review" || j.status === "analyzing" || j.status === "failed") && (
                          <Button size="sm" variant="outline" onClick={() => openJob(j.id)} data-testid={`hr-open-job-${j.id}`}>Openen</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* E-mail settings dialog */}
        <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>HR e-mailinstellingen</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>E-mailadres (mailbox)</Label>
                <Input data-testid="hr-email-address" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} placeholder="singhexpress68@gmail.com" /></div>
              <div><Label>IMAP-server</Label>
                <Input value={emailForm.imap_host} onChange={(e) => setEmailForm({ ...emailForm, imap_host: e.target.value })} placeholder="imap.gmail.com" /></div>
              <div><Label>Gmail app-wachtwoord</Label>
                <Input type="password" data-testid="hr-email-password" value={emailForm.app_password} onChange={(e) => setEmailForm({ ...emailForm, app_password: e.target.value })} placeholder={emailCfg?.has_password ? "•••••••• (ongewijzigd laten = leeg)" : "16-cijferig app-wachtwoord"} />
                <p className="text-xs text-muted-foreground mt-1">Maak een app-wachtwoord aan in je Google-account (Beveiliging → App-wachtwoorden). Het gewone wachtwoord werkt niet voor IMAP.</p>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                <Switch checked={emailForm.enabled} onCheckedChange={(v) => setEmailForm({ ...emailForm, enabled: v })} data-testid="hr-email-enabled" /> Automatisch ophalen aanzetten (live)
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailOpen(false)}>Annuleren</Button>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="hr-email-save" onClick={saveEmail}>Opslaan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // -------- failed --------
  if (job.status === "failed") {
    return (
      <div className="space-y-4" data-testid="hr-upload-failed">
        <Card><CardContent className="p-8 text-center space-y-4">
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto" />
          <p>{job.error || "Analyse mislukt."}</p>
          <div className="space-x-2">
            <Button variant="outline" onClick={reset}>Opnieuw beginnen</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={async () => { await api.post(`/hrdoc/uploads/${job.id}/retry`); setBusy(true); watch(job.id); }}>
              <RefreshCw className="h-4 w-4 mr-2" /> Opnieuw analyseren
            </Button>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  const empName = (id) => emps.find((e) => e.id === id)?.name || "";
  const allReviewed = rows.every((r) => r.reviewed);
  const skippedRows = rows.filter((r) => r.skip);

  // -------- review screen --------
  return (
    <div className="space-y-6" data-testid="hr-review">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Controleren & opslaan</h1>
          <p className="text-muted-foreground mt-1">Corrigeer werknemer, categorie, type en periode. Klik op het voorbeeld om het document groot te bekijken. Selecteer bestanden om ze in bulk te verwijderen. Vink elke rij aan om te bevestigen.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="hr-review-cancel">Annuleren</Button>
          <Button variant="outline" onClick={saveDraft} data-testid="hr-save-draft">Concept bewaren</Button>
          <Button className="bg-teal-600 hover:bg-teal-700" disabled={committing || !allReviewed} onClick={commit} data-testid="hr-commit-btn">
            {committing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opslaan…</> : <>Alles opslaan</>}
          </Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-2 z-10 flex items-center justify-between gap-3 bg-teal-600 text-white rounded-md px-4 py-2 shadow" data-testid="hr-bulkbar">
          <span className="text-sm font-medium">{selected.size} bestand(en) geselecteerd</span>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setSelected(new Set())}>Selectie wissen</Button>
            <Button size="sm" variant="destructive" onClick={() => deleteRows([...selected])} data-testid="hr-bulk-delete">
              <Trash2 className="h-4 w-4 mr-1" /> Verwijder geselecteerde
            </Button>
          </div>
        </div>
      )}

      {!allReviewed && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-2 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Bevestig elke rij vóór opslaan.
        </div>
      )}

      {skippedRows.length > 0 && (
        <div className="text-sm text-muted-foreground bg-muted/50 border rounded-md px-4 py-2 flex items-center justify-between">
          <span>{skippedRows.length} bestand(en) worden niet bewaard.</span>
          <Button size="sm" variant="ghost" onClick={() => setShowSkipped((v) => !v)} data-testid="hr-toggle-skipped">{showSkipped ? "Verbergen" : "Toon"}</Button>
        </div>
      )}

      {showSkipped && skippedRows.map((r) => (
        <div key={r.row_id} className="flex items-center justify-between border rounded-md px-4 py-2 bg-muted/30 opacity-70" data-testid={`hr-skipped-${r.row_id}`}>
          <span className="text-sm">Pagina {r.start_page}{r.end_page > r.start_page ? `–${r.end_page}` : ""} · {empName(r.employee_id) || "onbekend"}</span>
          <Button size="sm" variant="outline" onClick={() => restoreRow(r.row_id)} data-testid={`hr-restore-row-${r.row_id}`}>Herstellen</Button>
        </div>
      ))}

      {job.sources.map((source) => {
        const visible = rows.filter((r) => r.source_id === source.id && !r.skip);
        if (visible.length === 0 && rows.filter((r) => r.source_id === source.id).length > 0 && !showSkipped) {
          // all rows of this source are skipped — still allow adding a new one
        }
        return (
          <div key={source.id} className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-medium flex items-center gap-2"><ImageIcon className="h-4 w-4 text-muted-foreground" /> {source.name} <span className="text-muted-foreground font-normal">({source.page_count} pagina's)</span></div>
              <Button size="sm" variant="outline" onClick={() => addRow(source)} data-testid={`hr-addrow-${source.id}`}><Plus className="h-4 w-4 mr-1" /> Document toevoegen</Button>
            </div>
            {visible.map((r) => (
              <Card key={r.row_id} data-testid={`hr-row-${r.row_id}`} className={`${r.reviewed ? "border-teal-300" : ""} ${selected.has(r.row_id) ? "ring-2 ring-teal-500" : ""}`}>
                <CardContent className="p-4 grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
                  <div>
                    <div className="relative group cursor-zoom-in" onClick={() => { setZoom(100); setPreview({ sourceId: source.id, start: r.start_page, end: r.end_page }); }} data-testid={`hr-preview-open-${r.row_id}`}>
                      <PageImg jobId={job.id} sourceId={source.id} page={r.start_page} className="w-full rounded border" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded flex items-center justify-center transition-colors">
                        <span className="opacity-0 group-hover:opacity-100 bg-black/70 text-white text-xs rounded-full px-3 py-1 flex items-center gap-1"><Maximize2 className="h-3 w-3" /> Vergroten</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Label className="text-xs">Pag.</Label>
                      <Input type="number" min={1} max={source.page_count} className="h-8 w-16" data-testid={`hr-start-${r.row_id}`}
                             value={r.start_page} onChange={(e) => update(r.row_id, { start_page: Number(e.target.value) })} />
                      <span className="text-muted-foreground">–</span>
                      <Input type="number" min={1} max={source.page_count} className="h-8 w-16" data-testid={`hr-end-${r.row_id}`}
                             value={r.end_page} onChange={(e) => update(r.row_id, { end_page: Number(e.target.value) })} />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2 justify-between">
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={selected.has(r.row_id)} data-testid={`hr-select-${r.row_id}`} onCheckedChange={() => toggleSelect(r.row_id)} /> Selecteren
                        </label>
                        {r.possible_duplicate && <Badge className="bg-orange-100 text-orange-700 border-orange-200">Mogelijk duplicaat</Badge>}
                        {(r.warnings || []).map((w, i) => <Badge key={i} variant="outline" className="text-amber-600 border-amber-300 font-normal">{w}</Badge>)}
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeRow(r.row_id)} data-testid={`hr-removerow-${r.row_id}`}><Trash2 className="h-4 w-4 mr-1" /> Verwijderen</Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><Label className="text-xs">Werknemer</Label>
                        <Select value={r.employee_id || ""} onValueChange={(v) => update(r.row_id, { employee_id: v })}>
                          <SelectTrigger data-testid={`hr-emp-${r.row_id}`}><SelectValue placeholder="Kies werknemer" /></SelectTrigger>
                          <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Categorie</Label>
                        <Select value={r.category || ""} onValueChange={(v) => changeCategory(r.row_id, v)}>
                          <SelectTrigger data-testid={`hr-cat-${r.row_id}`}><SelectValue placeholder="Kies categorie" /></SelectTrigger>
                          <SelectContent>{HR_CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{HR_CATEGORIES[c]}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Type</Label>
                        <Select value={r.document_role || ""} disabled={!r.category} onValueChange={(v) => update(r.row_id, { document_role: v, document_type: `${r.category}_${v}` })}>
                          <SelectTrigger data-testid={`hr-role-${r.row_id}`}><SelectValue placeholder="Kies type" /></SelectTrigger>
                          <SelectContent>{rolesForCategory(r.category).map((role) => <SelectItem key={role} value={role}>{HR_ROLES[role]}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Periode</Label>
                        <Input data-testid={`hr-period-${r.row_id}`} value={r.period_key || ""} placeholder={periodHint(r.category)}
                               onChange={(e) => update(r.row_id, { period_key: e.target.value })} />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                      <Checkbox checked={r.reviewed} data-testid={`hr-reviewed-${r.row_id}`} onCheckedChange={(v) => update(r.row_id, { reviewed: !!v })} /> Gecontroleerd
                      {r.reviewed && <CheckCircle2 className="h-4 w-4 text-teal-600" />}
                    </label>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );
      })}

      {/* Grote documentviewer */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-4">
              <span>Documentvoorbeeld{preview ? ` · pagina ${preview.start}${preview.end > preview.start ? `–${preview.end}` : ""}` : ""}</span>
              <span className="flex items-center gap-1">
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setZoom((z) => Math.max(50, z - 25))} data-testid="hr-zoom-out"><Minus className="h-4 w-4" /></Button>
                <span className="text-xs w-12 text-center">{zoom}%</span>
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setZoom((z) => Math.min(300, z + 25))} data-testid="hr-zoom-in"><Plus className="h-4 w-4" /></Button>
              </span>
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="max-h-[76vh] overflow-auto bg-muted/40 rounded p-3 flex flex-col items-center gap-3" data-testid="hr-large-preview">
              {Array.from({ length: Math.max(1, (preview.end || preview.start) - preview.start + 1) }, (_, i) => preview.start + i).map((p) => (
                <div key={p} style={{ width: `${zoom}%` }} className="shrink-0">
                  <PageImg jobId={job.id} sourceId={preview.sourceId} page={p} className="w-full rounded border shadow-sm bg-white" />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
